import { useEffect, useMemo } from 'react';
import { useObservable } from './useObservable';
import {
  roomRateQuery,
  reservationQuery,
  roomRateService,
  Reservation,
  ReservationActivity,
  getPromoCode,
  getCostCenterOptionsService,
  getCostCenterOptionsQuery,
} from '@lib/state';
import { parseISO, startOfDay } from 'date-fns';

export function useRoomRates(userId?: string | null, corporateAccountId?: string) {
  const uiState = useObservable(roomRateQuery.uiState);
  const roomRates = useObservable(roomRateQuery.roomRates);
  const checkInDefaultDate = useObservable(reservationQuery.defaultCheckInDate);
  const corporateBooking = useObservable(roomRateQuery.corporateBooking);
  const policy = useObservable(roomRateQuery.policy);
  const propertyId = uiState.propertyId;
  const checkInDate = uiState.checkInDate ?? checkInDefaultDate;
  const checkOutDate = uiState.checkOutDate;
  const promoCode = uiState.promoCode;
  const quantity = uiState.quantity;
  const guestsPerRoom = uiState.guestsPerRoom ?? 1;
  const roomTypeId = uiState.roomTypeId;
  const checkInStartOfDay = checkInDate ? startOfDay(checkInDate) : undefined;
  const checkOutStartOfDay = checkOutDate ? startOfDay(checkOutDate) : undefined;
  const costCenterOptions = useObservable(getCostCenterOptionsQuery().costCenterOptions);
  const ratesLoading = useObservable(roomRateQuery.loading);
  const reservationsLoading = useObservable(reservationQuery.isLoading);
  const costCenterOptionsLoading = useObservable(getCostCenterOptionsQuery().isLoading);

  useEffect(() => {
    if (propertyId && checkInDate && checkOutDate && quantity && guestsPerRoom)
      roomRateService.getRoomRates(
        propertyId,
        checkInDate,
        checkOutDate,
        quantity * guestsPerRoom,
        promoCode,
        userId || undefined,
        corporateAccountId || undefined,
        roomTypeId
      );

    if (corporateAccountId) getCostCenterOptionsService().getCostCenterOptions(corporateAccountId);
  }, [
    propertyId,
    checkInDate,
    checkOutDate,
    quantity,
    guestsPerRoom,
    promoCode,
    userId,
    corporateAccountId,
    roomTypeId,
  ]);

  return {
    uiState,
    roomRates,
    checkInStartOfDay,
    checkOutStartOfDay,
    checkInDefaultDate,
    corporateBooking,
    quantity,
    policy,
    guestsPerRoom,
    roomTypeId,
    costCenterOptions,
    loading: ratesLoading || reservationsLoading || costCenterOptionsLoading,
    selectedRatePlan: roomTypeId ? roomRates?.find(r => r.roomTypeId === roomTypeId) : undefined,
  };
}

export function useRoomRatesByReservation(
  reservation: Reservation,
  activity: ReservationActivity,
  userId?: string | null,
  corporateAccountId?: string | null
) {
  const uiState = useObservable(roomRateQuery.uiState);
  const roomRates = useObservable(roomRateQuery.roomRates);
  const roomRatesChangeBilling = useObservable(roomRateQuery.roomRatesForChangeBilling);
  const loading = useObservable(roomRateQuery.loading);
  var promoCode = uiState.promoCode;
  const changeBillingRefunds = useObservable(roomRateQuery.changeBillingRefunds);

  // the api accepts promo code now and can override the promo code on the reservation
  // this only will occur in the api if the activity that is passed is 'ChangeBilling'
  // We need to override the default value of the promoCode with the promo code on the reservation
  // this only needs to occur once
  const promo = useMemo(() => getPromoCode(reservation), [reservation.id]);
  useEffect(() => {
    promoCode = promo;
  }, [promo]);

  useEffect(() => {
    roomRateService.getRoomRatesByReservation(
      reservation.propertyId,
      parseISO(reservation.checkInDate),
      parseISO(reservation.checkOutDate),
      reservation.id,
      reservation.rooms[0].roomTypeId,
      activity,
      promoCode,
      userId,
      corporateAccountId
    );
  }, [
    reservation.propertyId,
    reservation.checkInDate,
    reservation.checkOutDate,
    reservation.id,
    reservation.rooms[0].roomTypeId,
    activity,
    promoCode,
    userId,
    corporateAccountId,
  ]);

  return {
    roomRates: activity === ReservationActivity.ChangeBilling ? roomRatesChangeBilling : roomRates,
    promoCode,
    changeBillingRefunds,
    loading,
  };
}
